/* Build OHLC candles (portfolio NAV) + an S&P 500 overlay line from the same
   performance history the Performance card already fetches. The equity curve is
   one value per instant, so points are bucketed into a period coarser than their
   spacing and reduced to open/high/low/close — that's what gives each candle a
   real range:
     1D  (5-min points)  -> one candle per hour
     1W  (15-min points) -> one candle per calendar day
     1M  (daily points)  -> one candle per week
     ALL (daily points)  -> one candle per month
   The benchmark line takes one point per bucket (the bucket's last SPY value) so
   both series share an identical, ascending time axis. */
import type {
  PerformanceHistoryRange,
  PerformanceHistoryResponse,
} from '@shared/types';
import type { UTCTimestamp } from 'lightweight-charts';
import { aggregateNavToOhlc, type NavPoint } from './ohlc';
import type { TvCandle, TvLine } from './tvShared';

const DAY = 86_400; // seconds

/* Epoch-aligned bucket key for a UTC timestamp (seconds). Monotonic in time, so
   first-seen bucket order is chronological. Regular-session intraday bars all
   fall inside one UTC day, so day bucketing does not split a trading session. */
function bucketKeyFor(range: PerformanceHistoryRange, tsSec: number): string {
  if (range === '1D') return String(Math.floor(tsSec / 3_600)); // hourly
  if (range === '1W') return String(Math.floor(tsSec / DAY)); // daily
  if (range === '1M') return String(Math.floor(tsSec / (DAY * 7))); // weekly
  const d = new Date(tsSec * 1000); // monthly (ALL)
  return `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
}

export interface PortfolioCandleData {
  candles: TvCandle[];
  benchmark: TvLine[];
  benchmarkAvailable: boolean;
}

export function buildPortfolioCandles(
  history: PerformanceHistoryResponse | undefined,
  range: PerformanceHistoryRange,
): PortfolioCandleData {
  const points = history?.points ?? [];
  if (points.length === 0) {
    return { candles: [], benchmark: [], benchmarkAvailable: false };
  }

  // Representative time (each bucket's first timestamp) + rolling last benchmark
  // value, keyed by bucket, so candles carry a real ascending UTCTimestamp.
  const bucketTime = new Map<string, number>();
  const benchByBucket = new Map<string, number>();
  const navPoints: NavPoint[] = points.map((p) => {
    const key = bucketKeyFor(range, p.timestamp);
    if (!bucketTime.has(key)) bucketTime.set(key, p.timestamp);
    benchByBucket.set(key, p.benchmark);
    return { time: key, value: p.portfolio };
  });

  const ohlc = aggregateNavToOhlc(navPoints, (p) => p.time);

  const candles: TvCandle[] = ohlc.map((c) => ({
    time: bucketTime.get(c.time)! as UTCTimestamp,
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
  }));

  const benchmarkAvailable = history?.benchmarkAvailable === true;
  const benchmark: TvLine[] = benchmarkAvailable
    ? ohlc.map((c) => ({
        time: bucketTime.get(c.time)! as UTCTimestamp,
        value: benchByBucket.get(c.time)!,
      }))
    : [];

  return { candles, benchmark, benchmarkAvailable };
}
